import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, expiryStatus, STATUS_META, initials } from "@/lib/helpers";
import EmployeeDialog from "@/components/EmployeeDialog";
import BulkCourseDialog from "@/components/BulkCourseDialog";
import BulkEpiDialog from "@/components/BulkEpiDialog";
import BulkPayslipsDialog from "@/components/BulkPayslipsDialog";
import ExportDialog from "@/components/ExportDialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Users, Search, Plus, Pencil, Trash2, Wrench, Truck, CreditCard, GraduationCap, LayoutGrid, List, HardHat, Receipt, FileSpreadsheet } from "lucide-react";

export default function Employees() {
  const { isAdmin } = useAuth();
  const [emps, setEmps] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("activos");
  const [mode, setMode] = useState(() => localStorage.getItem("emp_view_mode") || "grid");
  useEffect(() => { localStorage.setItem("emp_view_mode", mode); }, [mode]);
  const [dialog, setDialog] = useState({ open: false, employee: null });
  const [toDelete, setToDelete] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkEpiOpen, setBulkEpiOpen] = useState(false);
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const load = () => api.get("/employees").then((r) => setEmps(r.data)).catch(() => setEmps([]));
  useEffect(() => { load(); }, []);

  const del = async () => {
    try {
      await api.delete(`/employees/${toDelete.id}`);
      toast.success("Empleado eliminado");
      setToDelete(null);
      load();
    } catch { toast.error("No se pudo eliminar"); }
  };

  if (!emps) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-52" />)}</div>;

  const isActive = (e) => e.active !== false;
  const activeCount = emps.filter(isActive).length;
  const filtered = emps.filter((e) =>
    (view === "todos" || (view === "activos" ? isActive(e) : !isActive(e))) &&
    (e.name.toLowerCase().includes(q.toLowerCase()) ||
    (e.position || "").toLowerCase().includes(q.toLowerCase()) ||
    (e.dni || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Empleados</h1>
          <p className="text-muted-foreground text-sm mt-1">{activeCount} activos · {emps.length - activeCount} de baja.</p>
        </div>
        <div className="flex gap-2" data-testid="employee-view-filter">
          {[["activos", "Activos"], ["bajas", "Bajas"], ["todos", "Todos"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`filter-view-${v}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                view === v ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Buscar nombre, puesto, DNI" value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="employee-search" />
          </div>
          {isAdmin && (
            <Button variant="outline" onClick={() => setBulkEpiOpen(true)} data-testid="bulk-epi-button">
              <HardHat className="h-4 w-4 mr-2" /> Entrega EPIs
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="export-employees-button">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Exportar listado
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={() => setBulkPayOpen(true)} data-testid="bulk-payslips-button">
              <Receipt className="h-4 w-4 mr-2" /> Nóminas del mes
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-course-button">
              <GraduationCap className="h-4 w-4 mr-2" /> Fechas de cursos
            </Button>
          )}
          <div className="flex rounded-md border overflow-hidden" data-testid="view-mode-toggle">
            <button onClick={() => setMode("grid")} data-testid="view-mode-grid" title="Tarjetas"
              className={`px-2.5 ${mode === "grid" ? "bg-primary text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}><LayoutGrid className="h-4 w-4" /></button>
            <button onClick={() => setMode("list")} data-testid="view-mode-list" title="Lista"
              className={`px-2.5 ${mode === "list" ? "bg-primary text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}><List className="h-4 w-4" /></button>
          </div>
          {isAdmin && (
            <Button onClick={() => setDialog({ open: true, employee: null })} data-testid="add-employee-button">
              <Plus className="h-4 w-4 mr-2" /> Nuevo
            </Button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground text-sm">
          <Users className="h-8 w-8 mx-auto mb-2 opacity-40" /> No hay empleados.
        </Card>
      ) : mode === "list" ? (
        <Card className="overflow-x-auto" data-testid="employees-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Empleado</th>
                <th className="text-left px-4 py-3">DNI</th>
                <th className="text-left px-4 py-3">Cad. DNI</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Teléfono</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Alta</th>
                <th className="text-center px-4 py-3 hidden md:table-cell">EPIs</th>
                <th className="text-center px-4 py-3 hidden md:table-cell">Cursos</th>
                <th className="text-center px-4 py-3 hidden md:table-cell">Vehículos</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((e) => {
                const st = expiryStatus(e.dni_expiry);
                const meta = STATUS_META[st];
                return (
                  <tr key={e.id} className="hover:bg-slate-50" data-testid={`employee-row-${e.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center text-xs font-semibold text-accent shrink-0 overflow-hidden">
                          {e.photo ? <img src={e.photo} alt="" className="h-full w-full object-cover" /> : initials(e.name)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate flex items-center gap-2">{e.name}
                            {!isActive(e) && <Badge className="bg-slate-200 text-slate-700 text-[10px]">Baja</Badge>}</p>
                          <p className="text-xs text-muted-foreground truncate">{e.position || "—"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{e.dni || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-2"><span className="font-mono text-xs">{fmtDate(e.dni_expiry)}</span>
                        {e.dni_expiry && <Badge className={`${meta.cls} text-[10px]`}>{meta.label}</Badge>}</span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs hidden md:table-cell">{e.phone || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs hidden lg:table-cell">{fmtDate(e.hire_date)}</td>
                    <td className="px-4 py-2.5 text-center hidden md:table-cell">{e.epis?.length || 0}</td>
                    <td className="px-4 py-2.5 text-center hidden md:table-cell">{e.prl_courses?.length || 0}</td>
                    <td className="px-4 py-2.5 text-center hidden md:table-cell">{e.vehicles?.length || 0}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDialog({ open: true, employee: e })} data-testid={`view-employee-${e.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => setToDelete(e)} data-testid={`delete-employee-${e.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="employees-grid">
          {filtered.map((e) => {
            const st = expiryStatus(e.dni_expiry);
            const meta = STATUS_META[st];
            return (
              <Card key={e.id} className="p-5 hover:shadow-md transition-shadow flex flex-col">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center font-semibold text-accent shrink-0 overflow-hidden">
                    {e.photo ? <img src={e.photo} alt="" className="h-full w-full object-cover" /> : initials(e.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold truncate flex items-center gap-2">{e.name}
                      {!isActive(e) && <Badge className="bg-slate-200 text-slate-700 text-[10px]" data-testid="employee-inactive-badge">Baja</Badge>}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">{e.position || "—"}</p>
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm flex-1">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-muted-foreground"><CreditCard className="h-3.5 w-3.5" /> DNI</span>
                    <span className="font-mono text-xs">{e.dni || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-xs">Caducidad DNI</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{fmtDate(e.dni_expiry)}</span>
                      {e.dni_expiry && <Badge className={`${meta.cls} text-[10px]`} data-testid="employee-card-dni-expiration-badge">{meta.label}</Badge>}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-4 pt-3 border-t text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Wrench className="h-3.5 w-3.5" /> {e.tools?.length || 0}</span>
                  <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> {e.vehicles?.length || 0}</span>
                  <span className="ml-auto flex gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDialog({ open: true, employee: e })}
                      data-testid={`view-employee-${e.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {isAdmin && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600"
                        onClick={() => setToDelete(e)} data-testid={`delete-employee-${e.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <EmployeeDialog open={dialog.open} employee={dialog.employee} isAdmin={isAdmin}
        onClose={() => setDialog({ open: false, employee: null })} onSaved={load} />
      <BulkCourseDialog open={bulkOpen} employees={emps} onClose={() => setBulkOpen(false)} onSaved={load} />
      <BulkEpiDialog open={bulkEpiOpen} employees={emps} onClose={() => setBulkEpiOpen(false)} onSaved={load} />
      <ExportDialog open={exportOpen} employees={emps} onClose={() => setExportOpen(false)} />
      <BulkPayslipsDialog open={bulkPayOpen} employees={emps} onClose={() => setBulkPayOpen(false)} onDone={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empleado?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará a <b>{toDelete?.name}</b> y todos sus registros. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={del} data-testid="confirm-delete-button">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
